import { Stack } from 'component-library';
import { FC, HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { TrendLabel as TrendLabelEnum } from '@/api/rest/resources/conservation';
import { ConservationBenchmark } from '@/api/rest/resources/types/fact';
import { INDICATOR } from '@/api/rest/resources/types/IndicatorProduct';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { ChartLegendBar } from '@/components/Charts';
import { CustomActiveBar } from '@/components/Charts/components/CustomActiveBar';
import { ChartLegendBarItem } from '@/components/Charts/components/types';
import { ChartProps } from '@/components/Charts/types';
import { getChartDataFromGraphFact, getXAxisDomainForHistoricGraph, sortChartData } from '@/components/Charts/utils';
import { BenchmarkPill } from '@/components/Pill/BenchmarkPill';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { usePlotGroupFilterStartYear } from '@/hooks/usePlotGroupFilterStartYear';
import { Logger } from '@/lib/logs/logger';
import { getBenchmarkForConservationFact } from '@/pages/shared/hooks/useBenchmarkForFact';
import {
  RenderTrendLine,
  TrendLabel,
} from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/ReferenceLine';
import { printYear } from '@/utils/formatting/date';
import { getColorForIndicator } from '@/utils/getColorForIndicator';

import { Bar, Chart, ComposedChart, getXAxisProps, TooltipProps } from './components/Chart';
import { RenderBenchmarkReferenceLine } from './components/ReferenceLine';
import { ChartTooltipTextPrimary, ChartTooltipTextSecondary } from './components/Tooltip';
import { NoChartData } from './NoChartData';

const HABITAT_INTACTNESS_LABEL = 'shared.ncaDetail.details.habitatIntactness.labels.habitatIntactness';
const UNIT = UnitEnum['%'];

const habitatIntactnessColors = getColorForIndicator(INDICATOR.HABITAT_INTACTNESS);
const chartColors = {
  hi: {
    bar: {
      fill: habitatIntactnessColors.fill,
    },
    line: {
      stroke: habitatIntactnessColors.stroke,
      dot: '#474D45',
    },
  },
} as const;

export type HabitatIntactnessChart = HTMLAttributes<HTMLDivElement> & {
  benchmark?: ConservationBenchmark | null;
  benchmarks?: Record<ConservationBenchmark, number | undefined>;
  trendLabel?: TrendLabelEnum | null;
  plotGroupId?: string;
} & Omit<ChartProps, 'benchmarks'>;

const factNames = {
  habitat_intactness_total: 'hiBar',
};

export const HabitatIntactnessHistorical: FC<HabitatIntactnessChart> = ({
  data,
  benchmark,
  benchmarks,
  trendLabel,
  plotGroupId,
  ...delegated
}) => {
  const { t } = useTranslation();
  const filtersStartYear = usePlotGroupFilterStartYear(plotGroupId);

  const dateDataMap = getChartDataFromGraphFact(data, factNames);
  const sortedChartData = sortChartData(dateDataMap, (date) => new Date(date).getTime());

  const firstTick = sortedChartData.at(0);
  const lastTick = sortedChartData.at(-1);

  if (!firstTick || !lastTick) {
    Logger.error('Not enough data to render the habitat intactness historical chart');
    return (
      <div className='h-full justify-center' {...delegated}>
        <NoChartData />
      </div>
    );
  }

  const initialYear = new Date(firstTick.label).getFullYear();

  const xTicksDomain = getXAxisDomainForHistoricGraph(
    firstTick.label,
    lastTick.label,
    filtersStartYear ?? initialYear,
    true,
  );
  const xAxisProps = {
    ...getXAxisProps(xTicksDomain),
    tickFormatter: printYear,
  };

  const chartData = sortedChartData.map((item) => ({
    ...item,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    hiLine: (item as any)[factNames.habitat_intactness_total],
    benchmark: benchmarks
      ? getBenchmarkForConservationFact({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          fact: { value: (item as any)[factNames.habitat_intactness_total]?.value },
          benchmarks,
        })
      : null,
  }));

  const graphBenchmark = benchmark && benchmarks && benchmark in benchmarks ? benchmarks[`${benchmark}`] : undefined;

  return (
    <>
      <Chart.Container
        {...delegated}
        data={chartData}
        config={{
          [factNames.habitat_intactness_total]: {
            label: t(HABITAT_INTACTNESS_LABEL),
            color: chartColors.hi.bar.fill,
          },
          hiLine: {
            label: t(HABITAT_INTACTNESS_LABEL),
            color: chartColors.hi.line.stroke,
          },
        }}
        data-testid='habitat-intactness-chart'
      >
        <ComposedChart data={chartData}>
          <Bar
            isAnimationActive={false}
            fill={chartColors.hi.bar.fill}
            dataKey={`${factNames.habitat_intactness_total}.value`}
            activeBar={<CustomActiveBar fill={chartColors.hi.bar.fill} />}
            barSize={20}
            className='hi-bar'
          />
          {trendLabel &&
            RenderTrendLine({
              dataKey: 'hiLine.value',
              stroke: chartColors.hi.line.stroke,
              label: (props) =>
                props.index === chartData.length - 1 && trendLabel ? (
                  <TrendLabel trend={trendLabel} lineColor={chartColors.hi.line.stroke} strokeWidth={2} {...props} />
                ) : (
                  <></>
                ),
              dotFill: chartColors.hi.line.dot,
            })}
          {graphBenchmark &&
            benchmark &&
            RenderBenchmarkReferenceLine({
              value: graphBenchmark,
              benchmark,
            })}
          <Chart.XAxis {...xAxisProps} padding={{ left: 40, right: 100 }} className='hi-x-axis' />
          <Chart.YAxis domain={[0, 100]} className='hi-y-axis'>
            <Chart.Label>{UNIT}</Chart.Label>
          </Chart.YAxis>
          <Chart.Tooltip cursor={false} content={(props) => <CustomTooltip {...props} />} />
        </ComposedChart>
      </Chart.Container>
      <CustomLegendBar trendLabel={trendLabel} />
    </>
  );
};

const CustomLegendBar = ({ trendLabel }: { trendLabel?: TrendLabelEnum | null }) => {
  const { t } = useTranslation();

  const legendBarItems: ChartLegendBarItem[] = [
    {
      label: t(HABITAT_INTACTNESS_LABEL),
      color: chartColors.hi.bar.fill,
      shape: 'column',
    },
  ];

  if (trendLabel) {
    legendBarItems.push({
      label: t('shared.ncaDetail.habitatIntactness.evolutionBU.trend'),
      color: chartColors.hi.line.stroke,
      shape: 'line',
    });
  }

  return (
    <ChartLegendBar items={legendBarItems} className='sm:ml-[28px]' data-testid='habitat-intactness-chart-legend' />
  );
};

const CustomTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
  const { t } = useTranslation();

  if (!active || !payload?.length) return null;

  const linePayload = payload.find((data) => data.dataKey === 'hiBar.value');
  const hiDisplayValue =
    linePayload?.value !== null && linePayload?.value !== undefined
      ? `${getDisplayNumber(linePayload.value as string, window.navigator.language)} ${UNIT}`
      : t('global.analysis.noData');

  const benchmark = linePayload?.payload?.benchmark;

  return (
    <Stack direction='col' spacing={1.5} className='rounded-md bg-white-100 px-2 py-1 elevation-1'>
      <Stack spacing={2}>
        <ChartTooltipTextSecondary>{printYear(label)}</ChartTooltipTextSecondary>
        {benchmark && <BenchmarkPill className='typography-body2' benchmarkType={benchmark} />}
        <Stack spacing={8} direction='row'>
          <ChartTooltipTextPrimary>{t(HABITAT_INTACTNESS_LABEL)}</ChartTooltipTextPrimary>
          <ChartTooltipTextPrimary>{hiDisplayValue}</ChartTooltipTextPrimary>
        </Stack>
      </Stack>
    </Stack>
  );
};

export { CustomTooltip as HabitatIntactnessHistoricalCustomTooltip };
